import { useCallback, useEffect, useState } from 'react';
import { fetchProjectmapDetail, fetchProjectmapQueue, type ProjectmapQueue, type ProjectmapQueueRow } from '@/api/projectmap';
import { formatUploadedAt } from '@/utils/collectors';
import { ProjectmapDetailPanel, type DetailState } from './ProjectmapDetailPanel';

/**
 * "Projecten maken": every job whose "Projectmap aanmaken" task is still open.
 * Clicking a row looks in SharePoint for that one job — on demand and one at a
 * time, because SharePoint is a single live tenant that throttles.
 */
export function ProjectenMaken() {
  const [queue, setQueue] = useState<ProjectmapQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ProjectmapQueueRow | null>(null);
  const [details, setDetails] = useState<Record<number, DetailState>>({});
  const loadingAny = Object.values(details).some((d) => d.status === 'loading');

  useEffect(() => {
    let cancelled = false;
    fetchProjectmapQueue()
      .then((q) => {
        if (!cancelled) setQueue(q);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Onbekende fout');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback((projectId: number) => {
    setDetails((prev) => ({ ...prev, [projectId]: { status: 'loading' } }));
    fetchProjectmapDetail(projectId)
      .then((detail) => setDetails((prev) => ({ ...prev, [projectId]: { status: 'ready', detail } })))
      .catch((err: unknown) =>
        setDetails((prev) => ({
          ...prev,
          [projectId]: { status: 'error', message: err instanceof Error ? err.message : 'Onbekende fout' },
        })),
      );
  }, []);

  const open = (row: ProjectmapQueueRow) => {
    setSelected(row);
    const known = details[row.projectId];
    if (!known && !loadingAny) load(row.projectId);
  };

  const selectedState: DetailState | undefined = selected ? details[selected.projectId] : undefined;

  return (
    <div className="px-4 lg:px-8 pb-8 mt-3 lg:mt-5 space-y-3">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}
      {!queue && !error && <p className="text-center text-td-muted text-sm py-12" role="status">Projecten laden...</p>}
      {queue && (
        <>
          <p className="text-td-muted text-xs lg:text-sm">
            {queue.rows.length} {queue.rows.length === 1 ? 'project' : 'projecten'} met een open taak &ldquo;Projectmap aanmaken&rdquo;
          </p>
          {queue.rows.length === 0 ? (
            <p className="text-center text-td-muted text-sm py-12">Er zijn geen projectmappen te maken.</p>
          ) : (
            <div className={selected ? 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_26rem] items-start' : ''}>
              <ul className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 order-last lg:order-first">
                {queue.rows.map((row) => {
                  const active = selected?.taskId === row.taskId;
                  return (
                    <li key={row.taskId}>
                      <button
                        type="button"
                        onClick={() => open(row)}
                        disabled={loadingAny && !details[row.projectId]}
                        aria-pressed={active}
                        className={`w-full text-left px-4 py-3 grid gap-x-4 gap-y-0.5 items-baseline transition hover:bg-slate-50 disabled:opacity-60 disabled:cursor-wait ${
                          // With the panel open the list is narrower: two columns instead of four, so nothing is clipped.
                          selected ? 'sm:grid-cols-2' : 'sm:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_7rem_minmax(0,1.2fr)]'
                        } ${active ? 'bg-td-blue/5' : ''}`}
                      >
                        <span className="font-semibold text-td-navy truncate" title={row.projectName}>
                          {row.saleOrderProjectName ?? row.projectName}
                        </span>
                        <span className="text-sm text-td-navy truncate">{row.klant ?? <span className="text-td-muted">geen klant</span>}</span>
                        <span className="text-sm text-td-navy">{row.saleOrderName ?? <span className="text-td-muted">geen SO</span>}</span>
                        <span className="text-sm text-td-muted">
                          {row.projectleider ?? 'geen projectleider'}
                          {row.createdAt && <span className="whitespace-nowrap"> · {formatUploadedAt(row.createdAt)}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {selected && (
                <ProjectmapDetailPanel
                  row={selected}
                  state={selectedState ?? { status: 'loading' }}
                  onRetry={() => {
                    if (!loadingAny) load(selected.projectId);
                  }}
                  onClose={() => setSelected(null)}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
